import i18next from 'i18next';
import {
  checkValidData,
  checkValidPrice,
  serialNumberFormatter,
} from '../../components/UIElements/Formatter';
import {
  getInputSearchFilter,
  getMultiSelectSearchFilter,
} from '../../components';
import { showDateInBrowser, readMoreTextShow } from '../../utils';
import { accessRoute } from '../../routes/Reports';
import { providerListService } from '../TeamSetting/provider.service';

export const outstandingPaymentsBreadcrumb = () => {
  return [
    {
      path: '/admin/dashboard',
      name: i18next.t('text.common.dashboard'),
    },
    {
      path: '#',
      name: i18next.t('text.report.outstandingPaymentsHeading'),
    },
  ];
};

export const outstandingPaymentsDetailBreadcrumb = () => {
  return [
    {
      path: '/admin/dashboard',
      name: i18next.t('text.common.dashboard'),
    },
    {
      path: accessRoute.OUTSTANDING_PAYMENTS.path,
      name: i18next.t('text.report.outstandingPaymentsHeading'),
    },
    {
      path: '#',
      name: i18next.t('text.report.customerOutstandingDetailHeading'),
    },
  ];
};

export const outstandingPaymentsTableColumn = ({
  onSort,
  onFilterChange,
  filter,
  page,
  sizePerPage,
  Action,
}) => {
  return [
    {
      dataIndex: 'id',
      title: i18next.t('text.common.serialNo'),
      headerClasses: 'w_70',
      render: (cell, row, index) =>
        serialNumberFormatter({ page, sizePerPage, index }),
      exportFormatter: (cell, row, index) =>
        serialNumberFormatter({ page, sizePerPage, index }),
    },
    {
      dataIndex: 'firstName',
      filterDataIndex: ['customerName'],
      title: i18next.t('text.report.customerNameInfo'),
      sorter: true,
      render: (cell, row) =>
        checkValidData(
          `${row?.firstName || ''} ${row?.lastName || ''}`.trim()
        ),
      exportFormatter: (cell, row) =>
        `${row?.firstName || ''} ${row?.lastName || ''}`.trim(),
      ...getInputSearchFilter({
        dataIndex: 'customerName',
        placeholder: i18next.t('text.common.searchPlaceholder') || 'Search customer name',
        onFilterChange,
        filter,
      }),
    },
    {
      dataIndex: 'email',
      filterDataIndex: ['email'],
      title: i18next.t('text.report.eMail'),
      sorter: true,
      render: (cell) => checkValidData(cell),
      ...getInputSearchFilter({
        dataIndex: 'email',
        placeholder: 'Search email',
        onFilterChange,
        filter,
      }),
    },
    {
      dataIndex: 'phoneNumber',
      filterDataIndex: ['phoneNumber'],
      title: i18next.t('text.report.phone'),
      sorter: false,
      render: (cell, row) => {
        if (row?.phoneNumber) {
          const code = row?.countryPhoneCode ? `(+${String(row.countryPhoneCode).replace('+', '')}) ` : '';
          return checkValidData(`${code}${row.phoneNumber}`);
        }
        return '-';
      },
      ...getInputSearchFilter({
        dataIndex: 'phoneNumber',
        placeholder: 'Search phone number',
        onFilterChange,
        filter,
      }),
    },
    {
      dataIndex: 'outstandingInvoiceCount',
      title: i18next.t('text.report.invoiceCount'),
      sorter: true,
      align: 'center',
      render: (cell) => checkValidData(cell),
    },
    {
      dataIndex: 'totalOutstanding',
      title: i18next.t('text.report.totalOutstanding'),
      sorter: true,
      render: (cell) => (
        <span className="text-danger fw-bold">{checkValidPrice(cell)}</span>
      ),
      exportFormatter: (cell) => checkValidPrice(cell),
    },
    {
      dataIndex: 'action',
      title: i18next.t('text.common.action'),
      export: false,
      render: (cell, row) => <Action row={row} cell={cell} />,
    },
  ];
};

export const outstandingPaymentsInvoiceTableColumn = ({
  page,
  sizePerPage,
  Action,
  onFilterChange,
  filter,
  onSort,
}) => {
  return [
    {
      dataIndex: 'id',
      title: i18next.t('text.common.serialNo'),
      headerClasses: 'w_70',
      render: (cell, row, index) =>
        serialNumberFormatter({ page: page || 1, sizePerPage: sizePerPage || 10, index }),
    },
    {
      dataIndex: 'bookingNo',
      title: i18next.t('text.report.bookingNo'),
      filterDataIndex: ['search'],
      sorter: false,
      render: (cell) => checkValidData(cell),
      ...getInputSearchFilter({
        dataIndex: 'search',
        placeholder: i18next.t('text.common.searchPlaceholder') || 'Search booking no',
        onFilterChange,
        filter,
      }),
    },
    {
      dataIndex: 'providerName',
      filterDataIndex: ['providerUserId'],
      title: i18next.t('text.report.providerName'),
      sorter: false,
      render: (cell, row) => {
        const provider = row?.provider || {};
        const fullName = `${provider?.firstName || ''} ${provider?.lastName || ''}`.trim();
        const email = provider?.email || '';

        if (!fullName && !email) return '-';

        return (
          <div className="user-info">
            <span>{fullName || '-'}</span>
            {/* {email && (
              <span className="text-muted d-block" style={{ fontSize: '12px' }}>
                <em className="icon ni ni-mail me-1" />
                {email}
              </span>
            )} */}
          </div>
        );
      },
      ...getMultiSelectSearchFilter({
        dataIndex: 'providerUserId',
        onFilterChange,
        name: 'provider name',
        mode: 'multiple',
        listService: providerListService,
        searchOrNot: true,
        filter,
        listKeys: { nameKey: ['firstName', 'lastName'], nameId: 'id' },
      }),
    },
    {
      dataIndex: 'bookingScheduleDate',
      title: i18next.t('text.report.jobsDate'),
      sorter: false,
      render: (cell, row) =>
        showDateInBrowser(row?.bookingScheduleDate || '--'),
    },
    {
      dataIndex: 'serviceEndAt',
      title: i18next.t('text.report.jobEndTime'),
      sorter: false,
      render: (cell, row) =>
        showDateInBrowser(row?.serviceEndAt || '--'),
    },
    {
      dataIndex: 'jobLocationAddress',
      title: i18next.t('text.report.location'),
      headerClasses: 'sorting',
      render: (cell, row) =>
        readMoreTextShow(
          checkValidData(row?.jobLocationAddress),
          i18next.t('text.report.location'),
          25
        ),
      width: 300,
    },    
    {
      dataIndex: 'total',
      title: i18next.t('text.report.invoiceAmount'),
      sorter: false,
      render: (cell) => checkValidPrice(cell),
    },
    {
      dataIndex: 'paidAmount',
      title: i18next.t('text.report.paidAmount'),
      sorter: false,
      render: (cell) => checkValidPrice(cell),
    },
    {
      dataIndex: 'remainingAmount',
      title: i18next.t('text.report.dueAmount'),
      sorter: false,
      render: (cell) => (
        <span className="text-danger fw-bold">{checkValidPrice(cell)}</span>
      ),
    },
    {
      dataIndex: 'action',
      title: i18next.t('text.common.action'),
      export: false,
      render: (cell, row) => <Action row={row} cell={cell} />,
    },
  ];
};
